import { useState } from 'react';
import { View, Text, Pressable, StyleSheet, useWindowDimensions } from 'react-native';
import { LineChart, BarChart, StackedBarChart } from 'react-native-chart-kit';

import LegendSection from '../LegendSection';
import { useTranslation } from '../../i18n';

const CHART_CONFIG = {
  backgroundColor: '#ffffff',
  backgroundGradientFrom: '#ffffff',
  backgroundGradientTo: '#ffffff',
  decimalPlaces: 0,
  color: (opacity = 1) => `rgba(0, 0, 0, ${opacity})`,
  style: {
    borderRadius: 16,
  },
  propsForVerticalLabels: {
    translateY: 15,
  },
};

const DEFAULT_VISIBLE = 5;

// Counts are small integers, so match segment count to the data range (max 5)
// to keep y-axis labels on whole numbers.
const getCountSegments = (maxValue) => Math.min(5, Math.max(1, maxValue));

const AnalyticsChart = ({
  title,
  data,
  segments,
  legendItems,
  onPress,
  isWeb = false,
  type = 'line',
}) => {
  const { t } = useTranslation('analytics');

  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const [showAll, setShowAll] = useState(false);

  // V3: Responsive chart sizing
  // Mobile: windowWidth - 24 (CollapsibleSection margins) - 32 (card padding) - 8 (content padding) = -64
  const chartWidth = isWeb
    ? windowWidth * 0.7 - 32  // Account for card padding
    : windowWidth - 72;       // Account for CollapsibleSection margins + card padding

  // Mobile height adapts to screen size for better visibility
  const mobileChartHeight = Math.min(Math.max(windowHeight * 0.28, 200), 280);
  const chartHeight = isWeb ? 650 : mobileChartHeight;
  const labelRotation = isWeb ? 0 : -90;

  // "Show more" limiting only applies to multi-line charts; bar/stacked data
  // arrives pre-shaped (e.g. top 5 + Other) from the screen.
  const hasMany = type === 'line' && legendItems.length > DEFAULT_VISIBLE;
  const visibleCount = showAll || !hasMany ? legendItems.length : DEFAULT_VISIBLE;

  // Limit both chart data and legend to visible count
  const displayLegendItems = legendItems.slice(0, visibleCount);
  const displayData = hasMany && !showAll
    ? {
        ...data,
        datasets: data.datasets.slice(0, visibleCount),
      }
    : data;

  // StackedBarChart labels can't rotate, so shrink them when buckets are dense
  const denseLabelProps =
    !isWeb && data.labels.length > 8
      ? { propsForVerticalLabels: { translateY: 15, fontSize: 9 } }
      : {};

  const renderChart = () => {
    if (type === 'bar') {
      const values = data.datasets[0]?.data || [0];
      const barColor = legendItems[0]?.color || 'rgba(0, 0, 0, 1)';
      return (
        <BarChart
          data={{ labels: data.labels, datasets: [{ data: values }] }}
          width={chartWidth + 28}
          height={chartHeight}
          segments={getCountSegments(Math.max(0, ...values))}
          fromZero
          showBarTops={false}
          yAxisLabel=""
          yAxisSuffix=""
          chartConfig={{
            ...CHART_CONFIG,
            ...denseLabelProps,
            barPercentage: 0.7,
            barRadius: 3,
            fillShadowGradientFrom: barColor,
            fillShadowGradientFromOpacity: 1,
            fillShadowGradientTo: barColor,
            fillShadowGradientToOpacity: 1,
          }}
          style={[styles.chart, { marginLeft: -28 }]}
        />
      );
    }

    if (type === 'stackedBar') {
      const rows = data.labels.map((_, i) =>
        data.datasets.map((dataset) => dataset.data?.[i] ?? 0)
      );
      const rowSums = rows.map((row) => row.reduce((a, b) => a + b, 0));
      const maxSum = Math.max(0, ...rowSums);

      // StackedBarChart divides by the max stack sum with no zero guard,
      // so an all-zero period must not reach it.
      if (maxSum === 0) {
        return (
          <View style={[styles.emptyChart, { height: chartHeight }]}>
            <Text style={styles.emptyChartText}>{t('noDataInPeriod')}</Text>
          </View>
        );
      }

      const barColors = data.datasets.map(
        (dataset, i) => (dataset.color ? dataset.color() : legendItems[i]?.color)
      );
      return (
        <StackedBarChart
          data={{ labels: data.labels, legend: [], data: rows, barColors }}
          width={chartWidth + 28}
          height={chartHeight}
          segments={getCountSegments(maxSum)}
          hideLegend
          decimalPlaces={0}
          chartConfig={{
            ...CHART_CONFIG,
            ...denseLabelProps,
            barPercentage: 0.7,
            barRadius: 3,
          }}
          style={[styles.chart, { marginLeft: -28 }]}
        />
      );
    }

    return (
      <LineChart
        data={displayData}
        verticalLabelRotation={labelRotation}
        horizontalLabelRotation={0}
        withShadow={false}
        width={chartWidth + 28}
        height={chartHeight}
        segments={segments}
        chartConfig={CHART_CONFIG}
        style={[styles.chart, { marginLeft: -28 }]}
      />
    );
  };

  // A single-series bar chart needs no legend — the title names the series
  const showLegend = !(type === 'bar' && legendItems.length <= 1);

  const chart = (
    <>
      {renderChart()}
      {showLegend && (
        <LegendSection
          legendItems={displayLegendItems}
          totalCount={legendItems.length}
          showAll={showAll}
          onToggle={hasMany ? () => setShowAll(!showAll) : null}
        />
      )}
    </>
  );

  const cardContent = (
    <>
      <Text style={styles.title}>{title}</Text>
      {chart}
    </>
  );

  return (
    <View style={styles.cardWrapper}>
      <View style={styles.card}>
        {onPress ? (
          <Pressable
            onPress={onPress}
            style={({ pressed, hovered, focused }) => [
              { cursor: 'pointer' },
              (hovered || focused) && styles.cardHovered,
              pressed && styles.pressed,
            ]}
          >
            {cardContent}
          </Pressable>
        ) : (
          cardContent
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  // V3: Card wrapper for consistent spacing
  cardWrapper: {
    paddingHorizontal: 12,
    marginBottom: 16,
  },
  // V3: Card styling matches other analytics cards
  card: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 8,
    elevation: 2,
    borderWidth: 1,
    borderColor: '#F0F0F0',
    overflow: 'hidden',
  },
  // V3: Visual feedback on press
  pressed: {
    opacity: 0.7,
  },
  // Web hover/focus feedback — subtle lift on the clickable chart card
  cardHovered: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.12,
    shadowRadius: 10,
    elevation: 3,
  },
  // V3: Title styling
  title: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 12,
  },
  chart: {
    marginVertical: 8,
    borderRadius: 16,
  },
  emptyChart: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyChartText: {
    fontSize: 14,
    color: '#6B7280',
  },
});

export { CHART_CONFIG };
export default AnalyticsChart;
